import clsx from "clsx";
import { useId } from "react";

export type ActiveEventVariant = "rose" | "purple" | "violet" | "amber";

/** Rounded-rect loop in viewBox 0..100 (matches tile shape, not a circle). */
const ORBIT = { x: 4, y: 4, w: 92, h: 92, r: 13 } as const;

function roundedRectLoop({ x, y, w, h, r }: typeof ORBIT): string {
  const x0 = x + r;
  const x1 = x + w - r;
  const y0 = y + r;
  const y1 = y + h - r;
  const cx = x + w / 2;
  return [
    `M ${cx} ${y}`,
    `L ${x1} ${y}`,
    `A ${r} ${r} 0 0 1 ${x + w} ${y0}`,
    `L ${x + w} ${y1}`,
    `A ${r} ${r} 0 0 1 ${x1} ${y + h}`,
    `L ${x0} ${y + h}`,
    `A ${r} ${r} 0 0 1 ${x} ${y1}`,
    `L ${x} ${y0}`,
    `A ${r} ${r} 0 0 1 ${x0} ${y}`,
    `L ${cx} ${y}`,
    "Z",
  ].join(" ");
}

const ORBIT_PATH = roundedRectLoop(ORBIT);

const VARIANT_STYLES: Record<
  ActiveEventVariant,
  {
    ring: string;
    glow: string;
    track: string;
    head: string;
    headGlow: string;
    tailBright: string;
    tailMid: string;
  }
> = {
  rose: {
    ring: "rgba(251, 113, 133, 0.85)",
    glow: "from-rose-500/35 to-orange-500/15",
    track: "rgba(254, 205, 211, 0.22)",
    head: "#ffe4e6",
    headGlow: "rgba(254, 205, 211, 0.95)",
    tailBright: "#fda4af",
    tailMid: "#fb7185",
  },
  purple: {
    ring: "rgba(192, 132, 252, 0.85)",
    glow: "from-purple-500/35 to-indigo-500/15",
    track: "rgba(233, 213, 255, 0.22)",
    head: "#f3e8ff",
    headGlow: "rgba(233, 213, 255, 0.95)",
    tailBright: "#d8b4fe",
    tailMid: "#c084fc",
  },
  violet: {
    ring: "rgba(167, 139, 250, 0.85)",
    glow: "from-violet-500/35 to-fuchsia-500/15",
    track: "rgba(221, 214, 254, 0.22)",
    head: "#ede9fe",
    headGlow: "rgba(221, 214, 254, 0.95)",
    tailBright: "#c4b5fd",
    tailMid: "#a78bfa",
  },
  amber: {
    ring: "rgba(251, 191, 36, 0.85)",
    glow: "from-amber-500/35 to-orange-500/15",
    track: "rgba(254, 243, 199, 0.22)",
    head: "#fef3c7",
    headGlow: "rgba(254, 243, 199, 0.95)",
    tailBright: "#fde68a",
    tailMid: "#fbbf24",
  },
};

const ORBIT_DURATION_S = 4.2;
/** Each dot starts later on the same path → trail bends around corners behind the head. */
const TRAIL_DOTS = 18;
const TRAIL_LAG_STEP_S = 0.038;

function mixColor(bright: string, mid: string, t: number): string {
  return t > 0.45 ? bright : mid;
}

function PathComet({
  path,
  dur,
  begin,
  r,
  fill,
  fillOpacity,
  stroke,
  glow,
  blurFilter,
}: {
  path: string;
  dur: number;
  begin: string;
  r: number;
  fill: string;
  fillOpacity: number;
  stroke?: string;
  glow?: string;
  blurFilter?: string;
}) {
  return (
    <g>
      <circle
        cx={0}
        cy={0}
        r={r}
        fill={fill}
        fillOpacity={fillOpacity}
        stroke={stroke}
        strokeWidth={0.5}
        vectorEffect="non-scaling-stroke"
        filter={blurFilter}
        style={glow ? { filter: `drop-shadow(0 0 3px ${glow})` } : undefined}
      />
      <animateMotion
        dur={`${dur}s`}
        repeatCount="indefinite"
        rotate="auto"
        path={path}
        begin={begin}
      />
    </g>
  );
}

/** Comet runs along the rounded-rectangle edge of the home tile (not a circular orbit). */
export function ActiveEventRing({
  variant = "rose",
  className,
}: {
  variant?: ActiveEventVariant;
  className?: string;
}) {
  const uid = useId().replace(/:/g, "");
  const trailBlurId = `active-event-trail-blur-${uid}`;
  const v = VARIANT_STYLES[variant];

  return (
    <div
      className={clsx(
        "active-event-ring pointer-events-none absolute -inset-1.5 z-[5] overflow-visible",
        className,
      )}
      aria-hidden
    >
      <div
        className={clsx(
          "active-event-ring__glow absolute inset-0 rounded-xl bg-gradient-to-br opacity-70",
          v.glow,
        )}
      />
      <div
        className={clsx("active-event-ring__pulse absolute inset-0 rounded-xl border-2")}
        style={{
          borderColor: v.ring,
          boxShadow: `0 0 16px ${v.ring}`,
        }}
      />

      <svg
        className="absolute inset-0 h-full w-full overflow-visible"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden
      >
        <defs>
          <filter
            id={trailBlurId}
            x="-100%"
            y="-100%"
            width="300%"
            height="300%"
            colorInterpolationFilters="sRGB"
          >
            <feGaussianBlur stdDeviation="1.25" />
          </filter>
        </defs>

        <rect
          x={ORBIT.x}
          y={ORBIT.y}
          width={ORBIT.w}
          height={ORBIT.h}
          rx={ORBIT.r}
          ry={ORBIT.r}
          fill="none"
          stroke={v.track}
          strokeWidth={0.6}
          vectorEffect="non-scaling-stroke"
        />

        {Array.from({ length: TRAIL_DOTS }, (_, i) => {
          const step = i + 1;
          const t = step / (TRAIL_DOTS + 1);
          const lag = `${step * TRAIL_LAG_STEP_S}s`;
          const opacity = Math.max(0.04, 0.85 * (1 - t * 1.05));
          const radius = Math.max(1.1, 3.4 * (1 - t * 0.72));
          return (
            <PathComet
              key={step}
              path={ORBIT_PATH}
              dur={ORBIT_DURATION_S}
              begin={lag}
              r={radius}
              fill={mixColor(v.tailBright, v.tailMid, t)}
              fillOpacity={opacity}
              blurFilter={`url(#${trailBlurId})`}
            />
          );
        })}

        <PathComet
          path={ORBIT_PATH}
          dur={ORBIT_DURATION_S}
          begin="0s"
          r={3.6}
          fill={v.head}
          fillOpacity={1}
          stroke="rgba(255,255,255,0.55)"
          glow={v.headGlow}
        />
      </svg>
    </div>
  );
}
